import * as React from 'react';
import { useSelector } from 'react-redux';

import { IDealFeatureCard, EDealFeatureCardTypes } from '../../../types/promoInfo';
import { makeModalTextLink } from '../../../utils/modalTextLink';
import { selectIsM2FlowActive, selectIsAccreditiveActive } from '../../../selectors/promoInfo';

const accreditiveLink = makeModalTextLink('lc', 'Аккредитив от СберБанка');
const sbrLink = makeModalTextLink('sbr', 'Сервис безопасных расчётов Циан & Tinkoff');
const selfPaymentLink = makeModalTextLink('other', 'самостоятельные расчёты');
const courierDeliveryLink = makeModalTextLink('courierDelivery', 'представитель Удостоверяющего центра');
const signMeLink = makeModalTextLink('signMe', 'Sign.Me');
const bankRepresentativeLink = makeModalTextLink('bankRepresentative', 'Представитель банка');
const goskeyLink = makeModalTextLink('goskey', '«Госключ»');

/* istanbul ignore next */
export const useGetFeatures = (isSbrEnabled: boolean): IDealFeatureCard[] => {
  const isAccreditiveActive = useSelector(selectIsAccreditiveActive);
  /* TODO M2: Удалить условия с этим флагом после полноценного релиза флоу с М2 */
  const isM2FlowActive = useSelector(selectIsM2FlowActive);
  const getCalculationMethod = (): string => {
    if (isAccreditiveActive && isSbrEnabled) {
      return `${accreditiveLink}, ${sbrLink} или ${selfPaymentLink}`;
    }
    if (isAccreditiveActive) {
      return `${accreditiveLink} или ${selfPaymentLink}`;
    }
    if (sbrLink) {
      return `${sbrLink} или ${selfPaymentLink}`;
    }

    return selfPaymentLink;
  };

  const calculationMethod = getCalculationMethod();

  return React.useMemo(() => {
    return [
      {
        type: EDealFeatureCardTypes.InOffice,
        title: 'В вашем офисе',
        description: 'Пригласите клиентов к себе, чтобы им было спокойно и комфортно',
        image: {
          x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/cian_office_1x.jpg',
          x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/cian_office_2x.jpg',
        },
        modal: {
          title: 'Сделка в вашем офисе',
          description:
            'Пригласите клиентов к себе, чтобы им было спокойно и комфортно. Вы сможете провести всю сделку не выходя из собственного офиса.',
          details: [
            {
              title: 'Заявка',
              badge: 'В среднем 20 минут',
              details: [
                'Заполните паспортные и контактные данные каждого участника сделки.',
                'Укажите время и место, где хотели бы провести сделку.',
                `Выберите один из способов расчётов: ${calculationMethod}.`,
                'Договор купли-продажи вы готовите самостоятельно и загружаете в форму.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/map_marker_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/map_marker_2x.png',
              },
            },
            {
              title: 'Подписание документов',
              badge: 'В среднем 30 минут',
              details: [
                `В назначенное время и место ${courierDeliveryLink} доставит электронные подписи.`,
                `Участники сделки установят приложение ${signMeLink} и подпишут в нём документы.`,
                `${bankRepresentativeLink} по видеосвязи удостоверит участников и передаст нам документ, подтверждающий участие банка в сделке.`,
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/delivery_man_2x.png',
              },
            },
            {
              title: 'Безопасный расчёт',
              badge: 'В среднем 30 минут',
              details: ['Проведите взаиморасчёты выбранным способом.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_2x.png',
              },
            },
            {
              title: 'Регистрация в Росреестре',
              badge: 'В среднем 1 – 5 дней',
              details: ['Персональный помощник отправит документы в электронном виде в Росреестр.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_2x.png',
              },
            },
            {
              title: 'Перевод денег продавцу',
              badge: 'В среднем 1 день',
              details: [
                'После успешной регистрации выполните условия в зависимости от выбранного способа взаиморасчётов.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_2x.png',
              },
            },
          ],
        },
      },
      {
        type: EDealFeatureCardTypes.Remotely,
        title: 'В любой точке мира',
        description:
          'Один клиент в Беларуси, другой в Турции? Главное, чтобы был загранпаспорт нового образца, организуем всё полностью удалённо',
        image: {
          x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/anywhere_1x.jpg',
          x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/anywhere_2x.jpg',
        },
        modal: {
          title: 'Сделка в любой точке мира',
          description:
            'При наличии загранпаспорта нового образца, смартфона с NFC и SIM-карты с российским номером сделку можно провести из любого места — удалённой деревни или другой страны.',
          details: [
            {
              title: 'Заявка',
              badge: 'В среднем 20 минут',
              details: [
                'Заполните паспортные и контактные данные каждого участника сделки.',
                'У участников сделки из-за границы должны быть на руках: загранпаспорт нового образца, паспорт гражданина РФ или его фото / скан, смартфон с NFC и SIM-карта с Российским номером.',
                'Выберите способ получения электронной подписи через загранпаспорт и смартфон. Участникам сделки не обязательно встречаться в одном месте: они смогут подписать документы где угодно.',
                `Выберите один из способов расчётов: ${calculationMethod}.`,
                'Договор купли-продажи вы готовите самостоятельно и загружаете в форму.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/apply_anywhere_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/apply_anywhere_2x.png',
              },
            },
            {
              title: 'Подписание документов',
              badge: 'В среднем 30 минут',
              details: [
                `Участники сделки установят приложение ${goskeyLink}, просканируют биометрический чип в загранпаспорте смартфоном и получат подпись.`,
                'Персональный помощник направит документы на подписание в приложение «Госключ», и все участники подпишут документы.',
                `${bankRepresentativeLink} по видеосвязи удостоверит участников и передаст нам документ, подтверждающий участие банка в сделке.`,
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/foreign_passport_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/foreign_passport_2x.png',
              },
            },
            {
              title: 'Безопасный расчёт',
              badge: 'В среднем 30 минут',
              details: ['Проведите взаиморасчёты выбранным способом.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_2x.png',
              },
            },
            {
              title: 'Регистрация в Росреестре',
              badge: 'В среднем 1 – 5 дней',
              details: ['Персональный помощник отправит документы в электронном виде в Росреестр.'],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_2x.png',
              },
            },
            {
              title: 'Перевод денег продавцу',
              badge: 'В среднем 1 день',
              details: [
                'После успешной регистрации выполните условия в зависимости от выбранного способа взаиморасчётов.',
              ],
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_1x.png',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_2x.png',
              },
            },
          ],
        },
      },
      ...(isM2FlowActive
        ? [
            {
              type: EDealFeatureCardTypes.InVtbBank,
              title: 'В офисе Банка ВТБ',
              description:
                'Если вам привычнее проводить сделки в банке, можно провести в офисах Банка ВТБ в более чем 200 городах РФ',
              image: {
                x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/vtb_office_1x.jpg',
                x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_types/vtb_office_2x.jpg',
              },
              modal: {
                title: 'Сделка в офисе Банка ВТБ',
                description: 'Можно провести сделку с квартирой, апартаментами, либо с участком и жилым домом.',
                details: [
                  {
                    title: 'Заявка',
                    badge: 'В среднем 20 минут',
                    details: [
                      'Выберите удобный для вас офис ВТБ для проведения сделки.',
                      'Укажите дату и время, когда хотели бы провести сделку.',
                      'Скачайте готовый договор купли-продажи.',
                      'Покупатель оплатит услугу сделки.',
                    ],
                    image: {
                      x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_dialogue_1x.png',
                      x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_dialogue_2x.png',
                    },
                  },
                  {
                    title: 'Встреча в банке',
                    badge: 'В среднем 60 минут ',
                    details: [
                      'В день проведения сделки в офисе банка сотрудник банка удостоверит личности и проверит данные сделки.',
                      'Сотрудник банка выпустит электронные подписи всем участникам сделки.',
                      'Участники сделки подпишут документы электронными подписями.',
                    ],
                    image: {
                      x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/hand_shake_1x.png',
                      x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/hand_shake_2x.png',
                    },
                  },
                  {
                    title: 'Безопасный расчёт',
                    badge: 'В среднем 30 минут',
                    details: [
                      `Покупатель оплатит услугу ${makeModalTextLink(
                        'sbrVtb',
                        'расчётов через номинальный счёт в Банке ВТБ',
                      )}.`,
                      'Покупатель перечислит средства за объект недвижимости <br /> на номинальный счёт компании «Метр квадратный», партнёра Банка ВТБ.',
                    ],
                    image: {
                      x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_1x.png',
                      x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/vault_2x.png',
                    },
                  },
                  {
                    title: 'Регистрация в Росреестре',
                    badge: 'В среднем 1 – 5 дней',
                    details: ['Сотрудник банка отправит документы в электронном виде в Росреестр.'],
                    image: {
                      x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_1x.png',
                      x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/rosreestr_registration_2x.png',
                    },
                  },
                  {
                    title: 'Перевод денег продавцу',
                    badge: 'В среднем 1 день',
                    details: [
                      'После успешной регистрации выполните условия в зависимости <br /> от выбранного способа взаиморасчётов.',
                    ],
                    image: {
                      x1: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_1x.png',
                      x2: 'https://cdn.cian.site/frontend/e2e-form-assets/promo/deal_steps/home_and_money_2x.png',
                    },
                  },
                ],
              },
            },
          ]
        : []),
    ];
  }, [isM2FlowActive, calculationMethod]);
};
